import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Package, DollarSign, Mail, Loader2, KeyRound, Settings2, Truck, Search, Download } from "lucide-react";
import { OrderManageDialog } from "../components/admin/OrderManageDialog";
import { ChangePasswordDialog } from "../components/admin/ChangePasswordDialog";
import { WaitlistPanel } from "../components/admin/WaitlistPanel";
import { SalesSnapshot } from "../components/admin/SalesSnapshot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: "bg-[#FFF6E5] text-[#C9A45C]",
  processing: "bg-[#EDE3D6] text-[#C9A45C]",
  shipped: "bg-[#E3F2FD] text-[#1565C0]",
  delivered: "bg-[#E8F5E9] text-[#2E7D32]",
  cancelled: "bg-[#FDECEA] text-[#C62828]",
};

const formatAddress = (a) => {
  if (!a) return "—";
  return [a.line1, a.line2, [a.postal_code, a.city].filter(Boolean).join(" "), [a.state, a.country].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(", ");
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
};

const StatsCards = ({ data }) => (
  <div className="grid grid-cols-2 gap-5">
    <div className="bg-[#FFFDF9] rounded-xl border border-[rgba(58,42,37,0.08)] p-6" data-testid="stat-total-orders">
      <div className="flex items-center gap-2 text-[#9A8D85]">
        <Package size={16} />
        <span className="text-xs font-mono-accent uppercase tracking-[0.15em]">Paid orders</span>
      </div>
      <p className="font-serif-display text-4xl text-[#2B1F1B] mt-2">{data?.count ?? 0}</p>
    </div>
    <div className="bg-[#FFFDF9] rounded-xl border border-[rgba(58,42,37,0.08)] p-6" data-testid="stat-revenue">
      <div className="flex items-center gap-2 text-[#9A8D85]">
        <DollarSign size={16} />
        <span className="text-xs font-mono-accent uppercase tracking-[0.15em]">Revenue</span>
      </div>
      <p className="font-serif-display text-4xl text-[#2B1F1B] mt-2">
        ${((data?.total_revenue_cents ?? 0) / 100).toFixed(2)}
      </p>
    </div>
  </div>
);

const OrderRow = ({ o, onManage }) => (
  <tr className="border-b border-[rgba(58,42,37,0.05)] hover:bg-[#F8F4EC] transition-colors duration-200" data-testid={`order-row-${o.order_number}`}>
    <td className="px-4 py-4 font-mono-accent text-xs text-[#3A2A25] whitespace-nowrap">{o.order_number || "—"}</td>
    <td className="px-4 py-4 text-[#756A64] whitespace-nowrap">{formatDate(o.created_at)}</td>
    <td className="px-4 py-4 text-[#2B1F1B]">{o.customer_name || o.shipping_address?.name || "—"}</td>
    <td className="px-4 py-4 text-[#756A64]">{o.customer_email || "—"}</td>
    <td className="px-4 py-4 text-[#756A64] max-w-[220px]">{formatAddress(o.shipping_address)}</td>
    <td className="px-4 py-4 text-[#2B1F1B]">{o.quantity}</td>
    <td className="px-4 py-4 text-[#2B1F1B] whitespace-nowrap">${((o.amount_total || 0) / 100).toFixed(2)}</td>
    <td className="px-4 py-4">
      <span className="px-3 py-1 rounded-full bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-mono-accent uppercase tracking-[0.1em]">{o.payment_status}</span>
    </td>
    <td className="px-4 py-4">
      <span className={`px-3 py-1 rounded-full text-[10px] font-mono-accent uppercase tracking-[0.1em] ${STATUS_STYLES[o.fulfillment_status] || STATUS_STYLES.pending}`} data-testid={`fulfillment-badge-${o.order_number}`}>
        {o.fulfillment_status}
      </span>
    </td>
    <td className="px-4 py-4 text-[#756A64] whitespace-nowrap text-xs">
      {o.tracking_number ? `${o.carrier || ""} ${o.tracking_number}`.trim() : "—"}
    </td>
    <td className="px-4 py-4">
      <div className="flex items-center gap-2">
        <Mail size={15} className={o.confirmation_email_sent ? "text-[#2E7D32]" : "text-[#D9CCBD]"} title="Order confirmation" />
        <Truck size={15} className={o.shipping_email_sent ? "text-[#2E7D32]" : "text-[#D9CCBD]"} title="Shipping confirmation" />
      </div>
    </td>
    <td className="px-4 py-4">
      <button
        onClick={() => onManage(o)}
        className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[rgba(58,42,37,0.2)] text-[11px] font-mono-accent uppercase tracking-[0.1em] text-[#3A2A25] hover:bg-[#EDE3D6] transition-colors duration-300"
        data-testid={`manage-order-button-${o.order_number}`}
      >
        <Settings2 size={13} /> Manage
      </button>
    </td>
  </tr>
);

const HEADERS = ["Order #", "Date", "Customer", "Email", "Shipping address", "Qty", "Total", "Payment", "Fulfillment", "Tracking", "Emails", ""];

const STATUS_FILTERS = ["all", "pending", "processing", "shipped", "delivered", "cancelled"];

const OrderFilters = ({ search, setSearch, status, setStatus, onExport, exporting, exportDisabled }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5" data-testid="order-filters">
    <div className="relative flex-1 max-w-md">
      <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A8D85]" />
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search order #, customer or email…"
        className="w-full pl-11 pr-4 py-2.5 rounded-full bg-[#FFFDF9] border border-[rgba(58,42,37,0.12)] text-sm font-light text-[#3A2A25] placeholder:text-[#9A8D85] focus:outline-none focus:border-[#C9A45C] transition-colors duration-300"
        data-testid="order-search-input"
      />
    </div>
    <div className="flex flex-wrap gap-2" data-testid="order-status-filters">
      {STATUS_FILTERS.map((s) => (
        <button
          key={s}
          onClick={() => setStatus(s)}
          className={`px-4 py-2 rounded-full text-[10px] font-mono-accent uppercase tracking-[0.1em] border transition-colors duration-300 ${
            status === s
              ? "bg-[#3A2A25] text-white border-[#3A2A25]"
              : "bg-[#FFFDF9] text-[#756A64] border-[rgba(58,42,37,0.12)] hover:border-[#C9A45C]"
          }`}
          data-testid={`status-filter-${s}`}
        >
          {s}
        </button>
      ))}
    </div>
    <button
      onClick={onExport}
      disabled={exporting || exportDisabled}
      className="sm:ml-auto flex items-center gap-1.5 px-4 py-2 rounded-full border border-[rgba(58,42,37,0.2)] text-[11px] font-mono-accent uppercase tracking-[0.1em] text-[#3A2A25] hover:bg-[#EDE3D6] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed w-fit"
      data-testid="orders-export-button"
    >
      <Download size={13} /> {exporting ? "Exporting…" : "Export CSV"}
    </button>
  </div>
);

const filterOrders = (orders, search, status) => {
  const q = search.trim().toLowerCase();
  return orders.filter((o) => {
    if (status !== "all" && (o.fulfillment_status || "pending") !== status) return false;
    if (!q) return true;
    return [o.order_number, o.customer_name, o.shipping_address?.name, o.customer_email]
      .some((v) => (v || "").toLowerCase().includes(q));
  });
};

const OrdersTable = ({ orders, onManage, hasOrders }) => (
  <div className="bg-[#FFFDF9] rounded-xl border border-[rgba(58,42,37,0.08)] overflow-x-auto" data-testid="orders-table">
    {orders.length === 0 ? (
      <p className="p-10 text-sm font-light text-[#9A8D85]" data-testid="orders-empty-message">
        {hasOrders ? "No orders match your search or filter." : "No paid orders yet."}
      </p>
    ) : (
      <table className="w-full text-left text-sm min-w-[960px]">
        <thead>
          <tr className="border-b border-[rgba(58,42,37,0.1)]">
            {HEADERS.map((h) => (
              <th key={h || "actions"} className="px-4 py-4 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#9A8D85] whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <OrderRow key={o.id} o={o} onManage={onManage} />
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manageOrder, setManageOrder] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const loadOrders = useCallback(() => {
    axios
      .get(`${API}/admin/orders`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => {
        // Not authenticated (or session expired) — the httpOnly cookie is the only credential.
        navigate("/admin/login", { state: { sessionExpired: true } });
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const exportOrdersCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/orders/export`, {
        withCredentials: true,
        responseType: "blob",
        params: { search, status },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `celusta-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Cookie may already be expired — redirect to login regardless.
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed; redirecting to login anyway", err);
      }
    }
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F4EC] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#C9A45C]" />
      </div>
    );
  }

  const orders = data?.orders || [];
  const filtered = filterOrders(orders, search, status);

  return (
    <div className="min-h-screen bg-[#F8F4EC]" data-testid="admin-dashboard-page">
      <header className="bg-[#FFFDF9] border-b border-[rgba(58,42,37,0.08)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-10 min-h-16 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-baseline gap-3 sm:gap-4">
            <span className="font-serif-display text-xl tracking-[0.25em] text-[#3A2A25]">CELUSTA</span>
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C9A45C]">Orders</span>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <button
              onClick={() => setShowPwd(true)}
              className="flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.15em] text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300"
              data-testid="change-password-button"
            >
              <KeyRound size={16} /> Password
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.15em] text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-10 py-10 overflow-x-hidden">
        <div className="grid lg:grid-cols-2 gap-5 mb-10">
          <StatsCards data={data} />
          <SalesSnapshot orders={orders} />
        </div>
        <OrderFilters
          search={search}
          setSearch={setSearch}
          status={status}
          setStatus={setStatus}
          onExport={exportOrdersCsv}
          exporting={exporting}
          exportDisabled={filtered.length === 0}
        />
        <OrdersTable orders={filtered} onManage={setManageOrder} hasOrders={orders.length > 0} />
        <WaitlistPanel />
      </main>

      {manageOrder && (
        <OrderManageDialog
          order={manageOrder}
          onClose={() => setManageOrder(null)}
          onSaved={() => loadOrders()}
        />
      )}
      {showPwd && <ChangePasswordDialog onClose={() => setShowPwd(false)} />}
    </div>
  );
}
